import {action, computed, decorate, observable} from 'mobx';
import {faHammer} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import {BaseModel} from './base';
import {transformation} from '../utils/transformations';
import {dateFormat, datetimeFormat, timeFormat} from '../config/moment';
import {CustomPropertyTypes} from './customProperty';
import i18n from '../i18n/i18n';
import {displayableProperty} from './displayableProperty';
import {displayablePropertyParam} from './displayablePropertyParam';
import {Material} from './material';
import {Order} from './order';
import {round} from '../utils/number';

const MAX_DECIMAL_PLACES = 3;

export class Operation extends BaseModel {
  static faIcon = faHammer;
  operationPhases = [];
  components = [];

  customPropertyType = CustomPropertyTypes.Operation;
  i18nPrefix = 'operation';

  reducedProperties = ['isFinished', 'isPlanned', 'isActive'];
  isPlanned = false;
  isActive = false;
  isFinished = false;

  displayableProperties = [
    displayableProperty({
      key: 'no',
      title: i18n.t('operation.model.attributes.no'),
      params: [displayablePropertyParam({path: 'no', transform: transformation.none})],
      template: '{value}',
    }),
    displayableProperty({
      key: 'name',
      title: i18n.t('operation.model.attributes.name'),
      params: [
        displayablePropertyParam({path: 'name', transform: transformation.none}),
      ],
      template: '{value}',
    }),
    displayableProperty({
      key: 'info',
      title: i18n.t('operation.model.attributes.info'),
      params: [
        displayablePropertyParam({path: 'info', transform: transformation.none}),
      ],
      template: '{value}',
    }),
    displayableProperty({
      key: 'plannedStartDate',
      title: i18n.t('operation.model.attributes.plannedStartDate'),
      params: [
        displayablePropertyParam({path: 'plannedStart', transform: transformation.datetime({format: dateFormat})}),
      ],
    }),
    displayableProperty({
      key: 'plannedStartTime',
      title: i18n.t('operation.model.attributes.plannedStartTime'),
      params: [
        displayablePropertyParam({path: 'plannedStart', transform: transformation.datetime({format: timeFormat})}),
      ],
    }),
    displayableProperty({
      key: 'plannedEndDate',
      title: i18n.t('operation.model.attributes.plannedEndDate'),
      params: [
        displayablePropertyParam({path: 'plannedEnd', transform: transformation.datetime({format: dateFormat})}),
      ],
    }),
    displayableProperty({
      key: 'plannedEndTime',
      title: i18n.t('operation.model.attributes.plannedEndTime'),
      params: [
        displayablePropertyParam({path: 'plannedEnd', transform: transformation.datetime({format: timeFormat})}),
      ],
    }),
    displayableProperty({
      key: 'actualStartDatetime',
      title: i18n.t('operation.model.attributes.actualStartDatetime'),
      params: [
        displayablePropertyParam({path: 'actualStart', transform: transformation.datetime({format: datetimeFormat})}),
      ],
      template: '{value}',
    }),
    displayableProperty({
      key: 'actualStartDate',
      title: i18n.t('operation.model.attributes.actualStartDate'),
      params: [
        displayablePropertyParam({path: 'actualStart', transform: transformation.datetime({format: dateFormat})}),
      ],
      template: '{value}',
    }),
    displayableProperty({
      key: 'actualStartTime',
      title: i18n.t('operation.model.attributes.actualStartTime'),
      params: [
        displayablePropertyParam({path: 'actualStart', transform: transformation.datetime({format: timeFormat})}),
      ],
    }),
    displayableProperty({
      key: 'actualEndDatetime',
      title: i18n.t('operation.model.attributes.actualEndDatetime'),
      params: [
        displayablePropertyParam({path: 'actualEnd', transform: transformation.datetime({format: datetimeFormat})}),
      ],
      template: '{value}',
    }),
    displayableProperty({
      key: 'actualEndDate',
      title: i18n.t('operation.model.attributes.actualEndDate'),
      params: [
        displayablePropertyParam({path: 'actualEnd', transform: transformation.datetime({format: dateFormat})}),
      ],
      template: '{value}',
    }),
    displayableProperty({
      key: 'actualEndTime',
      title: i18n.t('operation.model.attributes.actualEndTime'),
      params: [
        displayablePropertyParam({path: 'actualEnd', transform: transformation.datetime({format: timeFormat})}),
      ],
    }),
    displayableProperty({
      key: 'plannedDurationSeconds',
      title: i18n.t('operation.model.attributes.plannedDurationSeconds'),
      params: [
        displayablePropertyParam({path: 'plannedDurationSeconds', transform: transformation.duration()}),
      ],
      template: '{value}',
    }),
    displayableProperty({
      key: 'plannedQuantity',
      title: i18n.t('operation.model.attributes.plannedQuantity'),
      params: [
        displayablePropertyParam({path: 'plannedQuantity', transform: transformation.none, as: 'plannedQuantity'}),
        displayablePropertyParam({
          path: 'unitOfMeasure',
          transform: transformation.objectProperty('label'),
          as: 'unitOfMeasure',
        }),
      ],
      template: '{plannedQuantity} {unitOfMeasure}',
    }),
    displayableProperty({
      key: 'plannedScrap',
      title: i18n.t('operation.model.attributes.plannedScrap'),
      params: [
        displayablePropertyParam({path: 'plannedScrap', transform: transformation.none}),
      ],
      template: '{value}',
    }),
    displayableProperty({
      key: 'plannedSpeed',
      title: i18n.t('operation.model.attributes.plannedSpeed'),
      params: [
        displayablePropertyParam({path: 'plannedSpeed', transform: transformation.none}),
      ],
      template: '{value}',
    }),
    displayableProperty({
      key: 'quantityIsShould',
      title: i18n.t('operation.model.attributes.quantityIsShould'),
      params: [
        displayablePropertyParam({
          path: 'actualQuantity',
          transform: transformation.round(MAX_DECIMAL_PLACES),
          as: 'actualQuantity',
        }),
        displayablePropertyParam({
          path: 'plannedQuantity',
          transform: transformation.round(MAX_DECIMAL_PLACES),
          as: 'plannedQuantity',
        }),
        displayablePropertyParam({
          path: 'unitOfMeasure',
          transform: transformation.objectProperty('label'),
          as: 'unitOfMeasure',
        }),
      ],
      template: '{actualQuantity} / {plannedQuantity} {unitOfMeasure}',
    }),
    displayableProperty({
      key: 'status',
      title: i18n.t('operation.model.attributes.status'),
      params: [displayablePropertyParam({path: 'displayStatus'})],
      raw: true,
    }),
    displayableProperty({
      key: 'durationIsShould',
      title: i18n.t('operation.model.attributes.durationIsShould'),
      params: [
        displayablePropertyParam({path: 'plannedDurationSeconds', transform: transformation.duration(), as: 'should'}),
        displayablePropertyParam({path: 'usedDurationSeconds', transform: transformation.duration(), as: 'is'}),
      ],
      template: '{is} / {should}',
    }),
    displayableProperty({
      key: 'speedIsShould',
      title: i18n.t('operation.model.attributes.speedIsShould'),
      params: [
        displayablePropertyParam({
          path: 'plannedSpeed',
          transform: transformation.fallback(),
          as: 'should',
        }),
        displayablePropertyParam({
          path: 'actualSpeed',
          transform: transformation.fallback(),
          as: 'is',
        }),
      ],
      template: '{is} / {should}',
    }),
  ];

  static allDisplayableProperties(rootStore, pathPrefix = '', titlePrefix = '') {
    const allDisplayableProperties = super.allDisplayableProperties(rootStore, pathPrefix, titlePrefix);
    allDisplayableProperties.push(
      ...this.displayableCustomProperties(rootStore, CustomPropertyTypes.Operation, pathPrefix, titlePrefix)
    );

    allDisplayableProperties.push(
      ...Material.allDisplayableProperties(rootStore, `${pathPrefix}material.`, titlePrefix)
    );

    const orderPrefix = `${titlePrefix}${i18n.t('order.model.one')} > `;
    allDisplayableProperties.push(
      ...Order.allDisplayableProperties(rootStore, `${pathPrefix}order.`, orderPrefix)
    );

    return allDisplayableProperties;
  }

  populateAttributesFromStore(rootStore) {
    super.populateAttributesFromStore(rootStore);

    this.tmpSortOrder = this.sortOrder;

    this.setMaterial(rootStore);
    this.setUnitOfMeasure(rootStore);
    this.setOrder(rootStore);
    this.setOperationState(rootStore);
    this.setOperationPhases(rootStore);
    this.setComponents(rootStore);
    this.setDisplayStatus(rootStore);
    this.recalculateUsedDurationSeconds(rootStore);
  }

  recalculateUsedDurationSeconds(rootStore) {
    const endedStateLogs = rootStore.operationStateLogStore.logs.filter(
      (log) => log.operationId === this.id && log.end
    );
    const notEndedStateLogsByOperationId = rootStore.operationStateLogStore.logs.find(
      (log) => log.operationId === this.id && !log.end
    );

    if (!notEndedStateLogsByOperationId) {
      this.usedDurationSeconds = undefined;
      return;
    }

    const endedSeconds = endedStateLogs.reduce(
      (acc, current) => (current.durationSeconds ? acc + current.durationSeconds.asSeconds() : acc),
      0
    );
    const activeSeconds = (
      notEndedStateLogsByOperationId
        ? moment.duration(moment().diff(moment(notEndedStateLogsByOperationId.start), 'seconds'), 'seconds').asSeconds()
        : 0
    );

    this.usedDurationSeconds = endedSeconds + activeSeconds;
  }

  setDisplayStatus(rootStore) {
    this.displayStatus = (() => {
      if ((!rootStore.operationStore.active && this.isActive) || this.id === rootStore.operationStore.active?.id) {
        return Operation.displayStates.active;
      }
      if (this.disabled) {
        return Operation.displayStates.disabled;
      }
      if (this.state?.isAbort) {
        return Operation.displayStates.aborted;
      }
      if (this.state?.isFinal) {
        return Operation.displayStates.finished;
      }
      if (this.state?.isInitial && this.actualStart !== null) {
        return Operation.displayStates.paused;
      }
      if (!this.state?.isFinal) {
        return Operation.displayStates.planned;
      }
      return Operation.displayStates.none;
    })();
  }

  setComponents(rootStore) {
    this.components = rootStore.componentStore.components
      .filter((component) => component.operationId === this.id);
  }

  setOperationPhases(rootStore) {
    this.operationPhases = rootStore.operationPhaseStore.operationPhases
      .filter((phase) => phase.operationId === this.id);
  }

  setOperationState(rootStore) {
    this.state = rootStore.operationStateStore.getById(this.stateId);
  }

  setOrder(rootStore) {
    this.order = rootStore.orderStore.getById(this.orderId);
  }

  setUnitOfMeasure(rootStore) {
    this.unitOfMeasure = rootStore.unitOfMeasurementStore.getById(this.unitOfMeasureId);
  }

  setMaterial(rootStore) {
    this.material = rootStore.materialStore.getById(this.materialId);
  }

  get overtimeSeconds() {
    return (this.usedDurationSeconds - this.plannedDurationSeconds) < 0
      ? 0
      : (this.usedDurationSeconds - this.plannedDurationSeconds);
  }

  get availableSeconds() {
    return this.plannedDurationSeconds - this.usedDurationSeconds;
  }

  get usedDurationPercentage() {
    return this.overtimeSeconds <= 0
      ? (1 / this.plannedDurationSeconds) * this.usedDurationSeconds
      : (1 / (this.plannedDurationSeconds + this.overtimeSeconds)) * this.plannedDurationSeconds;
  }

  get availablePercentage() {
    return this.overtimeSeconds <= 0
      ? 1 - this.usedDurationPercentage
      : 0;
  }

  get overtimePercentage() {
    return this.overtimeSeconds <= 0
      ? 0
      : 1 - this.usedDurationPercentage;
  }

  get remainingQuantity() {
    return round(this.plannedQuantity - this.actualQuantity, MAX_DECIMAL_PLACES);
  }

  get expectedEnd() {
    if (!this.actualStart || !this.plannedDurationSeconds) {
      return undefined;
    }

    return moment(this.actualStart).add(this.plannedDurationSeconds, 'seconds');
  }
}

Operation.displayStates = {
  none: 'none',
  active: 'active',
  planned: 'planned',
  paused: 'paused',
  finished: 'finished',
  aborted: 'aborted',
  disabled: 'disabled',
};

decorate(Operation, {
  actualEnd: observable,
  actualQuantity: observable,
  actualScrap: observable,
  actualStart: observable,
  components: observable,
  createdAt: observable,
  createdBy: observable,
  disabled: observable,
  id: observable,
  info: observable,
  materialId: observable,
  material: observable,
  name: observable,
  no: observable,
  orderId: observable,
  order: observable,
  plannedDurationSeconds: observable,
  plannedEnd: observable,
  plannedQuantity: observable,
  plannedScrap: observable,
  plannedStart: observable,
  actualSpeed: observable,
  plannedSpeed: observable,
  properties: observable,
  sortOrder: observable,
  stateId: observable,
  state: observable,
  unitOfMeasureId: observable,
  unitOfMeasure: observable,
  updatedAt: observable,
  updatedBy: observable,
  workplaceId: observable,
  workplace: observable,
  displayStatus: observable,
  usedDurationSeconds: observable,
  operationPhases: observable,
  overtimeSeconds: computed,
  availableSeconds: computed,
  usedDurationPercentage: computed,
  availablePercentage: computed,
  overtimePercentage: computed,
  recalculateUsedDurationSeconds: action,
  remainingQuantity: computed,
  isPlanned: observable,
  isActive: observable,
  isFinished: observable,
  tmpSortOrder: observable,
  expectedEnd: computed,
});
